/**
 * Pure coverage computation for scenario generator.
 * Coverage = which combinations have at least one matching criterion.
 * Also reports criteria count per combination.
 */

const MAX_COMBINATIONS = 10000

/**
 * Get all possible values for a detail (enum/boolean only; range excluded).
 */
function getDetailValues(detail) {
  if (detail.type === 'boolean') return ['true', 'false', '_unknown']
  if (detail.type === 'enum' && detail.options && detail.options.length > 0) {
    const vals = detail.options.map(o => typeof o === 'string' ? o : o.id)
    vals.push('_unknown')
    return vals
  }
  return null
}

/**
 * Compute cartesian product of detail value arrays, respecting conditions.
 */
function generateCombinations(details, conditions) {
  const combinable = details
    .map(d => ({ id: d.id, values: getDetailValues(d) }))
    .filter(d => d.values && d.values.length > 0)

  if (combinable.length === 0) return []

  const condMap = new Map()
  for (const cond of conditions) {
    const depValues = typeof cond.depends_on_values === 'string'
      ? JSON.parse(cond.depends_on_values)
      : cond.depends_on_values
    if (!condMap.has(cond.detail_id)) condMap.set(cond.detail_id, [])
    condMap.get(cond.detail_id).push({
      dependsOnDetailId: cond.depends_on_detail_id,
      dependsOnValues: depValues,
    })
  }

  const combos = []
  function expand(index, current) {
    if (combos.length >= MAX_COMBINATIONS) return
    if (index >= combinable.length) {
      combos.push({ ...current })
      return
    }
    const detail = combinable[index]
    const conds = condMap.get(detail.id)
    if (conds) {
      const conditionMet = conds.every(c => {
        const depVal = current[c.dependsOnDetailId]
        return depVal !== undefined && c.dependsOnValues.includes(depVal)
      })
      if (!conditionMet) {
        expand(index + 1, current)
        return
      }
    }
    for (const val of detail.values) {
      current[detail.id] = val
      expand(index + 1, current)
    }
    delete current[detail.id]
  }

  expand(0, {})
  return combos
}

/**
 * Check if a criterion's scope matches a combination.
 * Same detail_id rows are OR'd, different detail_ids are AND'd.
 * Empty scope = universal.
 */
function criterionMatchesCombo(criterion, combo) {
  if (!criterion.scopes || criterion.scopes.length === 0) return true
  const groups = new Map()
  for (const s of criterion.scopes) {
    if (!groups.has(s.detail_id)) groups.set(s.detail_id, [])
    groups.get(s.detail_id).push(s.detail_value)
  }
  for (const [detailId, allowed] of groups) {
    const comboValue = combo[detailId]
    if (!allowed.includes(comboValue)) return false
  }
  return true
}

/**
 * Main coverage computation.
 *
 * @param {Array} criteria - array of { id, scopes: [{detail_id, detail_value}] }
 * @param {Array} relevantDetails - pack_details rows with id, type, options
 * @param {Array} conditions - detail_conditions rows
 * @returns {{ totalCombinations, coveredCombinations, percentage, uncoveredCombos, comboCounts, unscopedValues }}
 *   comboCounts: array of { combo, count } for every combination, sorted by count ascending
 *   unscopedValues: array of { detailId, value } pairs that no criterion's scope references
 */
export function computeCoverage(criteria, relevantDetails, conditions = []) {
  const combos = generateCombinations(relevantDetails, conditions)
  const totalCombinations = combos.length

  // Find detail values that no criterion's scope references
  const scopedPairs = new Set() // "detailId:value" strings
  for (const c of (criteria || [])) {
    for (const s of (c.scopes || [])) {
      scopedPairs.add(`${s.detail_id}:${s.detail_value}`)
    }
  }
  const combinableDetails = relevantDetails.filter(d => getDetailValues(d))
  const unscopedValues = [] // [{ detailId, value }]
  for (const d of combinableDetails) {
    const vals = getDetailValues(d)
    for (const v of vals) {
      if (!scopedPairs.has(`${d.id}:${v}`)) {
        unscopedValues.push({ detailId: d.id, value: v })
      }
    }
  }

  if (totalCombinations === 0) {
    return { totalCombinations: 0, coveredCombinations: 0, percentage: 100, uncoveredCombos: [], comboCounts: [], unscopedValues }
  }

  const uncoveredCombos = []
  const comboCounts = []
  let coveredCombinations = 0

  for (const combo of combos) {
    const count = (criteria || []).filter(c => criterionMatchesCombo(c, combo)).length
    comboCounts.push({ combo, count })
    if (count > 0) {
      coveredCombinations++
    } else {
      uncoveredCombos.push(combo)
    }
  }

  comboCounts.sort((a, b) => a.count - b.count)

  const percentage = totalCombinations > 0
    ? Math.round((coveredCombinations / totalCombinations) * 100)
    : 0

  return { totalCombinations, coveredCombinations, percentage, uncoveredCombos, comboCounts, unscopedValues }
}

/**
 * Summarize comboCounts into aggregate stats.
 *
 * @param {Array} comboCounts - array of { combo, count }
 * @returns {{ mean: number, median: number, thinCount: number, zeroCount: number, total: number, max: number }}
 */
export function summarizeComboCounts(comboCounts) {
  if (!comboCounts || comboCounts.length === 0) return null
  const counts = comboCounts.map(c => c.count)
  const sorted = [...counts].sort((a, b) => a - b)
  const sum = counts.reduce((a, b) => a + b, 0)
  const mean = sum / counts.length
  const mid = Math.floor(sorted.length / 2)
  const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
  const thinCount = counts.filter(c => c > 0 && c < 2).length
  const zeroCount = counts.filter(c => c === 0).length
  return { mean, median, thinCount, zeroCount, total: counts.length, max: sorted[sorted.length - 1] }
}

/**
 * Pivot comboCounts into a 2D matrix by two detail dimensions.
 * Each cell holds the minimum criteria count across all combinations
 * sharing that (row, col) value pair.
 *
 * @param {Array} comboCounts - array of { combo, count }
 * @param {string} rowDetailId
 * @param {string} colDetailId
 * @returns {{ rows: string[], cols: string[], cells: Object, maxMin: number }}
 */
export function pivotComboCounts(comboCounts, rowDetailId, colDetailId) {
  const cells = {} // { [rowVal]: { [colVal]: { min, total, n } } }
  const rowSet = new Set()
  const colSet = new Set()

  for (const { combo, count } of comboCounts) {
    const r = combo[rowDetailId]
    const c = combo[colDetailId]
    if (r === undefined || c === undefined) continue
    rowSet.add(r)
    colSet.add(c)
    if (!cells[r]) cells[r] = {}
    const prev = cells[r][c]
    if (!prev) {
      cells[r][c] = { min: count, total: count, n: 1 }
    } else {
      prev.min = Math.min(prev.min, count)
      prev.total += count
      prev.n++
    }
  }

  let maxMin = 0
  for (const r of rowSet) {
    for (const c of colSet) {
      if (cells[r]?.[c]) {
        maxMin = Math.max(maxMin, cells[r][c].min)
      }
    }
  }

  return { rows: [...rowSet], cols: [...colSet], cells, maxMin }
}
